import React, { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";
import { isIOS, isStandalone } from "../lib/pwa";

const DISMISS_KEY = "tadada.installDismissedAt";
const DISMISS_DAYS = 14;

/** Shows an "Install tadada" banner: native prompt on Android/desktop, guided steps on iOS Safari. */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);
  const [updateReg, setUpdateReg] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const recentlyDismissed = Date.now() - dismissedAt < DISMISS_DAYS * 86400000;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); if (!recentlyDismissed) setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS has no install event — surface a gentle hint after a short delay on Safari
    let t;
    if (isIOS() && !recentlyDismissed) t = setTimeout(() => setShow(true), 6000);
    const onUpdate = (e) => setUpdateReg(e.detail);
    window.addEventListener("tadada:sw-updated", onUpdate);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("tadada:sw-updated", onUpdate); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); setIosHelp(false); };
  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      setDeferred(null);
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };
  const applyUpdate = () => {
    updateReg?.waiting?.postMessage("SKIP_WAITING");
    navigator.serviceWorker?.addEventListener("controllerchange", () => window.location.reload(), { once: true });
  };

  if (updateReg) {
    return (
      <div className="fixed bottom-20 md:bottom-6 inset-x-4 md:inset-x-auto md:right-6 md:w-96 z-[60] rounded-2xl bg-[#0a1f4d] text-white p-4 shadow-2xl flex items-center gap-3" data-testid="pwa-update-banner">
        <p className="text-sm flex-1">A new version of tadada is ready.</p>
        <button onClick={applyUpdate} className="text-sm font-bold bg-white text-[#0a1f4d] rounded-full px-4 py-2" data-testid="pwa-update-btn">Update</button>
      </div>
    );
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 inset-x-4 md:inset-x-auto md:right-6 md:w-[420px] z-[60] rounded-2xl bg-white border border-[#E2E8F0] shadow-2xl p-4" data-testid="pwa-install-banner">
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl border border-[#E2E8F0]" />
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-[#0a1f4d]">Get the tadada app</p>
          <p className="text-xs text-[#64748B] mt-0.5">Add it to your home screen for one-tap booking, faster loading and full-screen trips.</p>
          {iosHelp && (
            <ol className="mt-3 space-y-1.5 text-xs text-[#0a1f4d]" data-testid="pwa-ios-steps">
              <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#F0F2F5] flex items-center justify-center text-[10px] font-bold">1</span>Tap the <Share size={13} className="inline text-[#1d7ffd]" /> <b>Share</b> button in Safari</li>
              <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#F0F2F5] flex items-center justify-center text-[10px] font-bold">2</span>Choose <PlusSquare size={13} className="inline text-[#1d7ffd]" /> <b>Add to Home Screen</b></li>
              <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#F0F2F5] flex items-center justify-center text-[10px] font-bold">3</span>Tap <b>Add</b> — done</li>
            </ol>
          )}
          <div className="flex gap-2 mt-3">
            {!iosHelp && <button onClick={install} className="btn-primary text-xs !py-2 !px-4" data-testid="pwa-install-btn"><Download size={14} /> {deferred ? "Install" : "How to install"}</button>}
            <button onClick={dismiss} className="text-xs font-semibold text-[#64748B] px-3 py-2" data-testid="pwa-install-dismiss">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-[#64748B] p-1" aria-label="Dismiss"><X size={16} /></button>
      </div>
    </div>
  );
}
